import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['link', 'section'];
  static values = {
    scrollTarget: { type: String, default: '' }
  };

  activeClasses = ['bg-emerald-700', 'text-emerald-50', 'shadow-sm', 'ring-1', 'ring-emerald-700/20'];
  inactiveClasses = ['text-slate-700'];

  connect() {
    this.sectionState = new Map();
    this.activeSectionId = null;
    this.hashHandler = this.syncFromHash.bind(this);
    this.boundTurboLoad = () => {
      this.syncFromHash();
      this.scrollSectionIntoView();
    };

    this.observer = new IntersectionObserver(this.handleIntersections.bind(this), {
      root: null,
      rootMargin: '-20% 0px -55% 0px',
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
    });

    this.sectionTargets.forEach((section) => {
      this.sectionState.set(section.id, {
        ratio: 0,
        isIntersecting: false
      });
      this.observer.observe(section);
    });

    window.addEventListener('hashchange', this.hashHandler);
    document.addEventListener('turbo:load', this.boundTurboLoad);

    this.syncFromHash();
    if (!this.activeSectionId) this.updateActiveLink(this.sectionTargets[0]?.id);

    requestAnimationFrame(() => this.scrollSectionIntoView());
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('hashchange', this.hashHandler);
    document.removeEventListener('turbo:load', this.boundTurboLoad);
  }

  scrollSectionIntoView() {
    const fromHash = window.location.hash.replace(/^#/, '');
    const fromFlash =
      typeof this.scrollTargetValue === 'string' && this.scrollTargetValue.trim().length > 0
        ? this.scrollTargetValue.trim()
        : '';
    const id = fromHash || fromFlash;
    if (!id) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    });
  }

  activateFromClick(event) {
    const sectionId = this.sectionIdFromLink(event.currentTarget);
    if (sectionId) this.updateActiveLink(sectionId);
  }

  handleIntersections(entries) {
    entries.forEach((entry) => {
      const id = entry.target.id;
      if (!id) return;

      this.sectionState.set(id, {
        ratio: entry.intersectionRatio,
        isIntersecting: entry.isIntersecting
      });
    });

    this.updateActiveFromViewport();
  }

  updateActiveFromViewport() {
    const intersecting = this.sectionTargets
      .map((section) => {
        const state = this.sectionState.get(section.id) || { ratio: 0, isIntersecting: false };
        return { section, ...state };
      })
      .filter(({ isIntersecting }) => isIntersecting);

    if (intersecting.length > 0) {
      intersecting.sort((a, b) => b.ratio - a.ratio);
      this.updateActiveLink(intersecting[0].section.id);
      return;
    }

    const marker = window.innerHeight * 0.3;
    const passed = this.sectionTargets
      .filter((section) => section.getBoundingClientRect().top <= marker)
      .at(-1);

    if (passed?.id) this.updateActiveLink(passed.id);
  }

  syncFromHash() {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    this.updateActiveLink(hash);
  }

  updateActiveLink(sectionId) {
    if (!sectionId || this.activeSectionId === sectionId) return;
    this.activeSectionId = sectionId;

    this.linkTargets.forEach((link) => {
      const matches = this.sectionIdFromLink(link) === sectionId;
      this.activeClasses.forEach((klass) => link.classList.toggle(klass, matches));
      this.inactiveClasses.forEach((klass) => link.classList.toggle(klass, !matches));
      link.setAttribute('aria-current', matches ? 'true' : 'false');
    });
  }

  sectionIdFromLink(link) {
    const href = link.getAttribute('href') || '';
    return href.startsWith('#') ? href.slice(1) : null;
  }
}
